import { AnimatePresence, motion } from "framer-motion"
import { createPortal } from "react-dom"
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import useAppStore from "../../../store/useAppStore"
import useLayoutStore from "../../../store/useLayoutStore"
import LoadingSpinner from "../../elements/LoadingSpinner"
import EmployeeMonthHoursLineChart, { NARROW_CHART_WIDTH_PX } from "./EmployeeMonthHoursLineChart"
import EmployeeMonthLogsCalendar from "./EmployeeMonthLogsCalendar"
import EmployeeMonthWorkSummary from "./EmployeeMonthWorkSummary"

const EmployeeDayLogTimeUpdateSuccessToast = () => {
    const { t } = useTranslation("stats")
    const isDarkTheme = useLayoutStore((state) => state.isDarkTheme)
    const setEmployeeDayLogTimeUpdateSuccessToast = useAppStore(
        (state) => state.setEmployeeDayLogTimeUpdateSuccessToast
    )

    const dismiss = () => setEmployeeDayLogTimeUpdateSuccessToast(false)

    useEffect(() => {
        const timer = setTimeout(dismiss, 5000)
        return () => clearTimeout(timer)
    }, [])

    const content = (
        <motion.div
            className={`dv-device-toast-container ${isDarkTheme ? "dark-profile-modal-bg-wrapper" : "white-profile-modal-bg-wrapper"}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={dismiss}
            style={{ pointerEvents: "auto", cursor: "pointer" }}
        >
            <motion.div
                className={`dv-device-toast-message ${isDarkTheme ? "dark-dv-device-toast-message" : "white-dv-device-toast-message"}`}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.2 }}
                style={{ pointerEvents: "none" }}
            >
                {t("toast.logTimeSaved")}
            </motion.div>
        </motion.div>
    )

    return createPortal(content, document.body)
}

const AdminEmployeeMonthLogsModal = () => {
    const isDarkTheme = useLayoutStore((state) => state.isDarkTheme)
    const userId = useAppStore((state) => state.employeeMonthLogsModalUserId)
    const employeeDayLogTimeUpdateSuccessToast = useAppStore(
        (state) => state.employeeDayLogTimeUpdateSuccessToast
    )
    const employeeMonthLogsModalDateFilter = useAppStore((state) => state.employeeMonthLogsModalDateFilter)
    const employeesData = useAppStore((state) => state.employeesData)
    const loadingEmployeeMonthLogs = useAppStore((state) => state.loadingEmployeeMonthLogs)
    const employeeMonthLogsByDay = useAppStore((state) => state.employeeMonthLogsByDay)
    const closeEmployeeMonthLogsModal = useAppStore((state) => state.closeEmployeeMonthLogsModal)

    const { year, month } = employeeMonthLogsModalDateFilter
    const [selectedDay, setSelectedDay] = useState(null)
    const [isNarrowModal, setIsNarrowModal] = useState(false)
    const modalContainerRef = useRef(null)

    useEffect(() => {
        setSelectedDay(null)
    }, [year, month, userId])

    useLayoutEffect(() => {
        const el = modalContainerRef.current
        if (!el) return undefined
        const sync = () => setIsNarrowModal(el.getBoundingClientRect().width <= NARROW_CHART_WIDTH_PX)
        sync()
        const observer = new ResizeObserver(sync)
        observer.observe(el)
        return () => observer.disconnect()
    }, [userId])

    const handleSelectDay = useCallback((day) => {
        setSelectedDay(day)
    }, [])

    const { fullName, avatarSrc } = useMemo(() => {
        const employee = userId ? employeesData?.[userId] : null
        const imageFile = employee?.image
        const ime = employee?.ime ?? ""
        const prezime = employee?.prezime ?? ""
        return {
            fullName: [ime, prezime].filter(Boolean).join(" "),
            avatarSrc: imageFile
                ? `${import.meta.env.VITE_API_IMAGES_URL}/employees/${imageFile}`
                : null
        }
    }, [userId, employeesData])

    useLayoutEffect(() => {
        if (!userId) return
        const handleClickOutside = (e) => {
            if (e.target.closest?.(".asv-emp-log-time-edit-layer")) return
            if (modalContainerRef.current && modalContainerRef.current.contains(e.target)) {
                return
            }
            closeEmployeeMonthLogsModal()
        }
        document.addEventListener("mousedown", handleClickOutside, true)
        return () => document.removeEventListener("mousedown", handleClickOutside, true)
    }, [userId, closeEmployeeMonthLogsModal])

    const modalContent = (
        <AnimatePresence>
            {userId ? (
                <motion.div
                    key={userId}
                    className="asv-eLogs-root"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.18, ease: "easeOut" }}
                >
                    <div
                        className={`asv-eLogs-backdrop ${isDarkTheme ? "asv-eLogs-backdrop--dark" : "asv-eLogs-backdrop--light"}`}
                        aria-hidden
                    />
                    <motion.div
                        ref={modalContainerRef}
                        className={`asv-employee-logs-modal ${isDarkTheme ? "asv-employee-logs-modal--dark" : "asv-employee-logs-modal--light"}`}
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.96 }}
                        transition={{ duration: 0.14, ease: "easeOut" }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="asv-employee-logs-modal-inner">
                            <div className="absence-modal-close-buttons-container">
                                <button
                                    type="button"
                                    className={`new-absence-modal-close-button ${isDarkTheme ? "filter-dark-buttons" : ""}`}
                                    onClick={closeEmployeeMonthLogsModal}
                                    aria-label="Close modal"
                                >
                                    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: "block", margin: "auto" }}>
                                        <line x1="7.2" y1="7.2" x2="20.8" y2="20.8" stroke="#08293e" strokeWidth="2.4" strokeLinecap="round" />
                                        <line x1="20.8" y1="7.2" x2="7.2" y2="20.8" stroke="#08293e" strokeWidth="2.4" strokeLinecap="round" />
                                    </svg>
                                </button>
                            </div>
                            <div className="asv-employee-logs-modal-header">
                                {avatarSrc ? (
                                    <img
                                        src={avatarSrc}
                                        alt=""
                                        className={`asv-employee-logs-modal-avatar ${isDarkTheme ? "dark-color" : ""}`}
                                        crossOrigin="use-credentials"
                                        onError={(e) => {
                                            e.target.src = "./views/login.svg"
                                        }}
                                    />
                                ) : (
                                    <div className={`asv-employee-logs-modal-avatar asv-employee-logs-modal-avatar--placeholder ${isDarkTheme ? "dark-color" : ""}`} />
                                )}
                                <div className="asv-employee-logs-modal-header-names">
                                    <p className={`asv-employee-logs-modal-name-line ${isDarkTheme ? "dark-color" : ""}`}>{fullName}</p>
                                </div>
                            </div>
                            <EmployeeMonthWorkSummary
                                logsByDay={employeeMonthLogsByDay}
                                year={year}
                                monthIndex={month}
                                isDarkTheme={isDarkTheme}
                                loading={loadingEmployeeMonthLogs}
                            />

                            <div className="asv-employee-logs-modal-body">
                                <div className="asv-employee-chart-container asv-employee-chart-container--with-loader">
                                    <AnimatePresence mode="wait">
                                        <motion.div
                                            key={`${employeeMonthLogsModalDateFilter.year}-${employeeMonthLogsModalDateFilter.month}`}
                                            className="asv-employee-chart-container-animated"
                                            initial={{ scale: 0.4, opacity: 0 }}
                                            animate={{ scale: 1, opacity: 1 }}
                                            exit={{ scale: 0.4, opacity: 0 }}
                                            transition={{ duration: 0.14, delay: 0.14 }}
                                            style={{ transformOrigin: "center center" }}
                                        >
                                            {loadingEmployeeMonthLogs ? (
                                                <div className="asv-employee-logs-modal-body-fill">
                                                    <LoadingSpinner />
                                                </div>
                                            ) : (
                                                <EmployeeMonthHoursLineChart
                                                    year={year}
                                                    monthIndex={month}
                                                    isDarkTheme={isDarkTheme}
                                                    logsByDay={employeeMonthLogsByDay}
                                                    onSelectDay={handleSelectDay}
                                                    isNarrow={isNarrowModal}
                                                />
                                            )}
                                        </motion.div>
                                    </AnimatePresence>
                                </div>
                                <div className="asv-employee-calendar-container">
                                    <EmployeeMonthLogsCalendar
                                        isDarkTheme={isDarkTheme}
                                        selectedDay={selectedDay}
                                        onSelectDay={handleSelectDay}
                                        modalContainerRef={modalContainerRef}
                                    />
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            ) : null}
        </AnimatePresence>
    )

    return (
        <>
            {createPortal(modalContent, document.body)}
            <AnimatePresence>
                {employeeDayLogTimeUpdateSuccessToast && (
                    <EmployeeDayLogTimeUpdateSuccessToast key="employee-day-log-time-toast" />
                )}
            </AnimatePresence>
        </>
    )
}

export default AdminEmployeeMonthLogsModal
